import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaEye, FaEdit, FaPrint, FaSearch, FaUpload } from 'react-icons/fa';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import Tesseract from 'tesseract.js';
import SiteLogo from '../../assets/images/SparkLogistics.jpg';
import { BASE_URL } from '../../api';

// ================== DYNAMIC SCHEMA ==================
const SCHEMA_FIELDS = [
  { key: 'manifestno', label: 'Manifest No', type: 'text', readonly: true },
  { key: 'routeid', label: 'Route', type: 'select', ref: 'routes', display: (r) => `${r.originid?.areaname || 'Origin'} → ${r.destinationid?.areaname || 'Dest'}` },
  { key: 'desthubid', label: 'Dest Hub', type: 'select', ref: 'branches', display: (b) => b.branchname || b._id },
  { key: 'ogdate', label: 'Date', type: 'date' },
  { key: 'ogtime', label: 'Time', type: 'time' },
  { key: 'modeid', label: 'Mode', type: 'select-enum', options: ['Air', 'Surface', 'Train'] },
  { key: 'transportid', label: 'Transport', type: 'select', ref: 'transports', display: (t) => t.vehicleno || t._id },
  { key: 'cocourier', label: 'Co-Courier', type: 'select', ref: 'transports', display: (t) => t.ownername || t._id },
  { key: 'coloader', label: 'Co-Loader', type: 'text' },
  { key: 'vehicletypeid', label: 'Vehicle Type', type: 'text' },
  { key: 'vehiclenumber', label: 'Vehicle No', type: 'text' },
  { key: 'drivername', label: 'Driver Name', type: 'text' },
  {
    key: 'status', label: 'Status', type: 'select-enum', options: [
      'Manifest Out - Intransit',
      'Manifest In - Reached at Destination',
      'Runsheet - Out of Delivery',
      'Delivered',
      'Pending',
      'Return to Hub',
      'Wrong Address'
    ]
  },
  { key: 'remarks', label: 'Remarks', type: 'textarea' }
];

const OutgoingManifest = () => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [manifests, setManifests] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [areas, setAreas] = useState([]);
  const [branches, setBranches] = useState([]);
  const [transports, setTransports] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [selectedManifest, setSelectedManifest] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState([]); // Full objects
  const [editedConsignors, setEditedConsignors] = useState({});
  const [editedBookingRemarks, setEditedBookingRemarks] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [previewManifestNo, setPreviewManifestNo] = useState('OGM-000001');
  const [mastersLoaded, setMastersLoaded] = useState(false);
  const itemsPerPage = 10;

  const [formData, setFormData] = useState({
    routeid: '', desthubid: '', ogdate: new Date().toISOString().split('T')[0],
    ogtime: '', modeid: '', transportid: '', cocourier: '', coloader: '',
    vehicletypeid: '', vehiclenumber: '', drivername: '',
    status: 'Manifest Out - Intransit', remarks: '', manifestno: ''
  });

  const token = localStorage.getItem('token');
  const API_BASE = `${BASE_URL}/api`;

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : 'N/A';

  // ========== NEXT MANIFEST NO ==========
  const calculateNextManifestNo = (last) => {
    if (!last?.manifestno) return 'OGM-000001';
    const match = last.manifestno.match(/OGM-(\d+)/);
    if (!match) return 'OGM-000001';
    const num = parseInt(match[1], 10) + 1;
    return `OGM-${num.toString().padStart(6, '0')}`;
  };

  const updatePreview = () => {
    if (manifests.length === 0) return;
    const sorted = [...manifests].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const nextNo = calculateNextManifestNo(sorted[0]);
    setPreviewManifestNo(nextNo);
    if (!formData._id) {
      setFormData(prev => ({ ...prev, manifestno: nextNo }));
    }
  };

  // ========== FETCH MASTERS ==========
  const fetchMasters = async () => {
    setIsLoading(true);
    try {
      const [routeRes, branchRes, transportRes, areaRes] = await Promise.all([
        fetch(`${API_BASE}/routemasters`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/master/branch`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/master/transport`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`${API_BASE}/master/area`, { headers: { Authorization: `Bearer ${token}` } })
      ]);

      const routeJson = await routeRes.json();
      const branchJson = await branchRes.json();
      const transportJson = await transportRes.json();
      const areaJson = await areaRes.json();

      setRoutes(routeJson.data || []);
      setAreas(areaJson.success ? areaJson.data : []);
      setBranches(branchJson.success ? branchJson.data : []);
      setTransports(transportJson.success ? transportJson.data : []);

      setMastersLoaded(true);   // ← important
    } catch (err) {
      toast.error('Failed to load masters');
    } finally {
      setIsLoading(false);
    }
  };
  // ========== FETCH MANIFESTS ==========
  const fetchManifests = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE}/outgoingmanifests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const json = await res.json();
      if (Array.isArray(json)) {
        setManifests(json);
        updatePreview();
      } else {
        toast.error(json.message || 'Invalid response');
      }
    } catch (err) {
      toast.error('Failed to load manifests');
    } finally {
      setIsLoading(false);
    }
  };

  // ========== FETCH BOOKINGS ==========
  const fetchBooked = async (docket = '') => {
    setIsLoading(true);
    try {
      const url = new URL(`${API_BASE}/onlinebookings/bookings/booked`);
      if (docket) url.searchParams.append('docketNumber', docket);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      let bookedData = json.data || [];

      // Merge fully populated bookings to fix missing pickrequestid.location for Origin
      try {
        const allRes = await fetch(`${API_BASE}/onlinebookings`, { headers: { Authorization: `Bearer ${token}` } });
        const allJson = await allRes.json();
        const allBooks = allJson.data || (Array.isArray(allJson) ? allJson : []);
        if (Array.isArray(allBooks)) {
          bookedData = bookedData.map(b => {
            const found = allBooks.find(ab => ab._id === b._id);
            return found ? { ...b, pickrequestid: found.pickrequestid, locationid: found.locationid } : b;
          });
        }
      } catch (e) {
        console.error('Failed to merge populated bookings', e);
      }

      setBookings(bookedData);
    } catch (err) {
      toast.error('Failed to fetch bookings');
      setBookings([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchMasters();
      fetchManifests();
      fetchBooked();
    }
  }, [token]);

  useEffect(() => {
    updatePreview();
  }, [manifests]);

  // ========== AUTO-FILL FROM ROUTE ==========
  const handleRouteChange = (e) => {
    const routeId = e.target.value;
    const route = routes.find(r => r._id === routeId);
    if (!route) return;

    setFormData(prev => ({
      ...prev,
      routeid: routeId,
      desthubid: route.destinationid?._id || '',
      modeid: route.modeid || '',
      transportid: route.transportid?._id || '',
      cocourier: route.cocourier || '',
      coloader: route.coloader || '',
      vehicletypeid: route.vehicletypeid === 1 ? 'Truck' : route.vehicletypeid === 2 ? 'Tempo' : 'Lorry',
      vehiclenumber: route.vehiclenumberText || '',
      drivername: route.drivername || '',
      ogdate: route.routedate ? new Date(route.routedate).toISOString().split('T')[0] : prev.ogdate,
      ogtime: route.routetiming || ''
    }));
  };

  // ========== SUBMIT (POST for create, PUT for update) ==========
  const handleSubmit = async () => {
    if (selectedBookings.length === 0) {
      return toast.error('Please select at least one booking');
    }

    const onlineIds = selectedBookings.map(b => b._id);

    const consignors = selectedBookings
      .map(b => editedConsignors[b._id] || 'N/A')
      .join(', ');

    const bookingremarks = selectedBookings
      .map(b => editedBookingRemarks[b._id] || '')
      .filter(Boolean)
      .join(' | ');

    const payload = {
      ...formData,
      online: onlineIds,
      consignors,
      bookingremarks,
    };

    ['routeid', 'desthubid', 'transportid'].forEach(key => {
      if (!payload[key]) {
        payload[key] = null;
      }
    });

    // Do NOT send manifestno when creating new → backend should generate it
    if (!formData._id) {
      delete payload.manifestno;
    }

    setIsLoading(true);

    try {
      let res;
      let successMessage;

      if (formData._id) {
        // UPDATE - PUT /outgoingmanifests/:id
        res = await fetch(`${API_BASE}/outgoingmanifests/${formData._id}`, {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        successMessage = 'Manifest updated successfully!';
      } else {
        // CREATE - POST /outgoingmanifests
        res = await fetch(`${API_BASE}/outgoingmanifests`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
        successMessage = 'Manifest created successfully!';
      }

      const json = await res.json();

      if (res.ok || json.success) {
        toast.success(successMessage);
        await fetchManifests();
        handleCloseForm();
      } else {
        toast.error(json.message || 'Save failed');
      }
    } catch (err) {
      console.error('Submit error:', err);
      toast.error('Failed to save manifest. Check your connection.');
    } finally {
      setIsLoading(false);
    }
  };

  // ========== PRINT ==========
  const handlePrint = async (manifest) => {
    try {
      let linked = manifest.online || [];

      // Try fetching full bookings to ensure populated data for PDF
      try {
        const res = await fetch(`${API_BASE}/onlinebookings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const json = await res.json();
        const allBookings = json.data || (Array.isArray(json) ? json : []);
        if (Array.isArray(allBookings)) {
          linked = linked.map(item => {
            const found = allBookings.find(b => b._id === (item._id || item) || b.docketNumber === item.docketNumber);
            return found ? { ...item, ...found } : item;
          });
        }
      } catch (err) {
        console.error("Failed to fetch full bookings for PDF mapping", err);
      }

      const doc = new jsPDF('p', 'mm', 'a4');
      const w = doc.internal.pageSize.getWidth();
      const h = doc.internal.pageSize.getHeight();
      const margin = 10;

      // 1. LOGO & HEADER
      doc.addImage(SiteLogo, 'JPEG', margin, 5, 40, 20);
      doc.setFontSize(10).setFont('helvetica', 'bold');
      doc.setTextColor(150, 0, 0); // Professional red for emphasis
      doc.text('OUTGOING MANIFEST', w - margin - 5, 18, { align: 'right' });

      doc.setFontSize(8).setFont('helvetica', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text(`Print Date: ${new Date().toLocaleString('en-GB')}`, w - margin - 5, 23, { align: 'right' });

      // Horizontal Line
      doc.setDrawColor(0, 51, 102);
      doc.setLineWidth(0.5);
      doc.line(margin, 28, w - margin, 28);

      // 2. PRIMARY INFO GRID
      const infoY = 32;
      doc.setFillColor(245, 247, 250);
      doc.rect(margin, infoY, w - (margin * 2), 25, 'F');

      doc.setFontSize(8).setFont('helvetica', 'bold').setTextColor(50, 50, 50);

      const col1 = margin + 2;
      const col2 = 80;
      const col3 = 145;
      const rowH = 6;

      // Labels
      doc.text('MANIFEST NO:', col1, infoY + 7);
      doc.text('DATE/TIME:', col1, infoY + 7 + rowH);
      doc.text('STATUS:', col1, infoY + 7 + (rowH * 2));

      doc.text('VEHICLE NO:', col2, infoY + 7);
      doc.text('CO-LOADER:', col2, infoY + 7 + rowH);
      doc.text('DEST HUB:', col2, infoY + 7 + (rowH * 2));

      doc.text('ROUTE:', col3, infoY + 7);
      doc.text('MODE:', col3, infoY + 7 + rowH);
      doc.text('TOTAL LOAD:', col3, infoY + 7 + (rowH * 2));

      // Values
      const routeObj = routes.find(r => r._id === (manifest.routeid?._id || manifest.routeid));
      const originName = routeObj?.originid?.areaname || manifest.routeid?.originid?.areaname || 'N/A';
      const destName = routeObj?.destinationid?.areaname || manifest.routeid?.destinationid?.areaname || 'N/A';
      
      const transportObj = transports.find(t => t._id === (manifest.transportid?._id || manifest.transportid));
      const vehicleNo = transportObj?.vehicleno || manifest.transportid?.vehicleno || manifest.vehiclenumber || 'N/A';
      
      const coLoaderName = manifest.coloader || 'N/A';
      
      const destHubObj = branches.find(b => b._id === (manifest.desthubid?._id || manifest.desthubid));
      const destHubName = destHubObj?.branchname || manifest.desthubid?.branchname || 'N/A';

      const modeMap = { '1': 'Air', '2': 'Surface', '3': 'Train' };
      const modeText = modeMap[manifest.modeid] || manifest.modeid?.toUpperCase() || 'SURFACE';

      doc.setFont('helvetica', 'normal').setTextColor(0, 0, 0);
      doc.text(manifest.manifestno || 'N/A', col1 + 22, infoY + 7);
      doc.text(`${new Date(manifest.ogdate).toLocaleDateString('en-GB')} ${manifest.ogtime || ''}`, col1 + 22, infoY + 7 + rowH);
      
      // Truncate/wrap status to prevent overlapping col2 (DEST HUB)
      const statusText = manifest.status || 'Intransit';
      const formattedStatus = doc.splitTextToSize(statusText, 42)[0];
      doc.text(formattedStatus, col1 + 22, infoY + 7 + (rowH * 2));

      doc.text(vehicleNo, col2 + 22, infoY + 7);
      doc.text(coLoaderName, col2 + 22, infoY + 7 + rowH);
      doc.text(destHubName, col2 + 22, infoY + 7 + (rowH * 2));

      doc.text(`${originName} TO ${destName}`, col3 + 22, infoY + 7);
      doc.text(modeText, col3 + 22, infoY + 7 + rowH);

      const totalBoxes = linked.reduce((sum, item) => sum + (Number(item.noofbox) || 0), 0);
      const totalWeight = linked.reduce((sum, item) => sum + (Number(item.actweight) || 0), 0).toFixed(2);
      doc.text(`${totalBoxes} Boxes / ${totalWeight} Kg`, col3 + 22, infoY + 7 + (rowH * 2));

      // 3. TABLE DATA
      const tableRows = linked.map((item, index) => {
        const consignorNames = manifest.consignors ? manifest.consignors.split(',').map(s => s.trim()) : [];
        const customConsignee = consignorNames[index] && consignorNames[index] !== 'N/A' ? consignorNames[index] : null;

        const consignorName = item.pickrequestid?.clientid?.companyName || 
                              item.pickrequestid?.clientid?.name || 
                              item.consignorid?.companyName || 
                              item.consignorid?.name || 
                              item.clientid?.companyName || 
                              item.clientid?.name || 
                              (typeof item.consignorid === 'string' ? item.consignorid : null) ||
                              item.consignor || 
                              'N/A';

        const rawConsignee = item.consigneeTOaddress || 
                             item.consigneeid?.companyName || 
                             item.consigneeid?.name || 
                             (typeof item.consigneeid === 'string' && item.consigneeid !== 'N/A' ? item.consigneeid : null) || 
                             item.consignee;

        let consigneeName = 'N/A';

        if (customConsignee && customConsignee.trim() !== '' && customConsignee !== 'N/A' && customConsignee !== consignorName) {
          consigneeName = customConsignee;
        } else if (rawConsignee && rawConsignee.trim() !== '' && rawConsignee !== 'N/A' && rawConsignee !== consignorName) {
          consigneeName = rawConsignee;
        }

        return [
          index + 1,
          item.docketNumber || 'N/A',
          consigneeName,
          consignorName,
          item.noofbox || 0,
          item.actweight ? Number(item.actweight).toFixed(2) : '0.00',
          item.topincode?.locationname || 'N/A',
          item.topincode?.pincode || 'N/A',
          item.ewaybill || 'N/A'
        ];
      });

      autoTable(doc, {
        startY: infoY + 30,
        margin: { left: margin, right: margin },
        head: [['S.No', 'Docket No', 'Consignee', 'Consignor', 'Box', 'Weight', 'Destination', 'Pincode', 'E-Way Bill']],
        body: tableRows,
        theme: 'grid',
        headStyles: {
          fillColor: [0, 51, 102],
          textColor: 255,
          fontSize: 8,
          fontStyle: 'bold',
          halign: 'center'
        },
        styles: {
          fontSize: 7,
          cellPadding: 2,
          halign: 'center',
          lineColor: [200, 200, 200],
          lineWidth: 0.1
        },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          1: { cellWidth: 22, halign: 'left', fontStyle: 'bold' },
          2: { cellWidth: 35, halign: 'left' },
          3: { cellWidth: 35, halign: 'left' },
          4: { cellWidth: 10, halign: 'center' },
          5: { cellWidth: 15, halign: 'right' },
          6: { cellWidth: 28, halign: 'left' },
          7: { cellWidth: 15, halign: 'center' },
          8: { cellWidth: 20, halign: 'center' }
        },
        didDrawPage: (data) => {
          // Footer
          doc.setFontSize(7).setTextColor(150, 150, 150);
          doc.text('Note: This is a computer generated document and does not require a physical signature for verification.', margin, h - 10);
          doc.text(`Page ${data.pageNumber}`, w - margin - 10, h - 10);
        }
      });

      // 4. SIGNATURE SECTION
      const finalY = doc.lastAutoTable.finalY + 20;
      if (finalY + 30 > h) {
        doc.addPage();
        var currentY = 20;
      } else {
        var currentY = finalY;
      }

      doc.setFontSize(9).setFont('helvetica', 'bold').setTextColor(0, 0, 0);

      const sigW = 50;
      const sigSpacing = (w - (margin * 2) - (sigW * 3)) / 2;

      // Dispatcher
      doc.line(margin, currentY + 15, margin + sigW, currentY + 15);
      doc.text('Dispatcher Signature', margin + sigW / 2, currentY + 20, { align: 'center' });

      // Driver
      doc.line(margin + sigW + sigSpacing, currentY + 15, margin + sigW * 2 + sigSpacing, currentY + 15);
      doc.text('Driver Signature', margin + sigW + sigSpacing + sigW / 2, currentY + 20, { align: 'center' });

      // Receiver
      doc.line(w - margin - sigW, currentY + 15, w - margin, currentY + 15);
      doc.text('Receiving Hub Auth.', w - margin - sigW / 2, currentY + 20, { align: 'center' });

      doc.save(`Outgoing_Manifest_${manifest.manifestno}.pdf`);
      toast.success('Professional Manifest Generated');
    } catch (err) {
      console.error("PDF Mapping Error:", err);
      toast.error('Failed to generate manifest PDF');
    }
  };


  // ========== EDIT ==========
  const handleEdit = async (m) => {
    setFormData({
      _id: m._id,
      routeid: m.routeid?._id || m.routeid || '',
      desthubid: m.desthubid?._id || m.desthubid || '',
      ogdate: m.ogdate ? new Date(m.ogdate).toISOString().split('T')[0] : '',
      ogtime: m.ogtime || '',
      modeid: m.modeid || '',
      transportid: m.transportid?._id || m.transportid || '',
      cocourier: m.cocourier || '',
      coloader: m.coloader || '',
      vehicletypeid: m.vehicletypeid || '',
      vehiclenumber: m.vehiclenumber || '',
      drivername: m.drivername || '',
      status: m.status || 'Manifest Out - Intransit',
      remarks: m.remarks || '',
      manifestno: m.manifestno || ''
    });

    let manifestBookings = Array.isArray(m.online) ? m.online : [];

    // Ensure all items in manifestBookings are full objects
    try {
      const res = await fetch(`${API_BASE}/onlinebookings`, { headers: { Authorization: `Bearer ${token}` } });
      const json = await res.json();
      const allBooks = json.data || (Array.isArray(json) ? json : []);
      if (Array.isArray(allBooks)) {
        manifestBookings = manifestBookings.map(b => {
          const bId = b._id || b;
          const found = allBooks.find(ab => ab._id === bId);
          return found ? { ...(typeof b === 'object' ? b : {}), ...found } : (typeof b === 'object' ? b : { _id: bId });
        });
      }
    } catch (e) {
      console.error('Failed to fetch full online bookings for edit', e);
    }

    setSelectedBookings(manifestBookings);

    // Merge manifest's bookings into the overall bookings selection list
    fetchBooked().then(() => {
      setBookings(prevBooked => {
        const combined = [...manifestBookings];
        prevBooked.forEach(b => {
          if (!combined.some(cb => cb._id === b._id)) {
            combined.push(b);
          }
        });
        return combined;
      });
    });

    // Parse consignors & bookingremarks
    const consignorList = (m.consignors || '').split(',').map(s => s.trim());
    const remarkList = (m.bookingremarks || '').split('|').map(s => s.trim());

    const cons = {};
    const rem = {};
    manifestBookings.forEach((b, i) => {
      const bookingConsignor = b.pickrequestid?.clientid?.companyName || b.pickrequestid?.clientid?.name || b.consignorid?.companyName || b.consignorid?.name;
      const savedConsignee = consignorList[i];
      const actualConsignee = b.consigneeTOaddress || b.consigneeid?.companyName || b.consigneeid?.name || '';
      
      const cName = (savedConsignee && savedConsignee !== 'N/A' && savedConsignee !== bookingConsignor) 
        ? savedConsignee 
        : actualConsignee;

      cons[b._id] = cName;
      rem[b._id] = remarkList[i] || b.remarks || '';
    });

    setEditedConsignors(cons);
    setEditedBookingRemarks(rem);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setSelectedBookings([]);
    setEditedConsignors({});
    setEditedBookingRemarks({});
    setFormData({
      routeid: '', desthubid: '', ogdate: new Date().toISOString().split('T')[0],
      ogtime: '', modeid: '', transportid: '', cocourier: '', coloader: '',
      vehicletypeid: '', vehiclenumber: '', drivername: '',
      status: 'Manifest Out - Intransit', remarks: '', manifestno: ''
    });
  };

  const preprocessImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.width = img.width;
          canvas.height = img.height;
          ctx.drawImage(img, 0, 0);
          
          const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
          const data = imageData.data;
          
          for (let i = 0; i < data.length; i += 4) {
            const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
            // threshold to black & white
            const val = avg > 140 ? 255 : 0; 
            data[i] = val;
            data[i + 1] = val;
            data[i + 2] = val;
          }
          ctx.putImageData(imageData, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileUpload = async (e, bookingId) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Set temporary loading state
    setEditedConsignors(prev => ({ ...prev, [bookingId]: 'Extracting...' }));

    try {
      // 1. Preprocess the image (make it black & white to improve Tesseract accuracy)
      const processedImage = await preprocessImage(file);

      // 2. Run Tesseract on the processed image
      const { data: { text } } = await Tesseract.recognize(
        processedImage,
        'eng',
        { logger: m => console.log(m) }
      );

      // 3. Clean up the garbage characters - keep only alphanumeric and spaces
      const cleanedText = text.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, ' ').trim();

      setEditedConsignors(prev => ({ ...prev, [bookingId]: cleanedText }));
      
      if (cleanedText) {
        toast.success('Text extracted successfully!');
      } else {
        toast.warning('No legible text found in image');
      }
    } catch (err) {
      console.error('OCR Error:', err);
      setEditedConsignors(prev => ({ ...prev, [bookingId]: '' }));
      toast.error('Failed to extract text from image');
    }

    e.target.value = null;
  };

  // ========== DYNAMIC FIELD RENDERER ==========
  const renderField = (field) => {
    const value = formData[field.key] || '';
    const isReadonly = field.readonly;

    const handleChange = (e) => {
      const val = e.target.value;
      if (field.key === 'routeid') {
        handleRouteChange(e);
      } else {
        setFormData(prev => ({ ...prev, [field.key]: val }));
      }
    };

    if (field.type === 'select') {
      const options = field.ref === 'routes' ? routes :
        field.ref === 'branches' ? branches :
          field.ref === 'transports' ? transports : [];

      return (
        <select value={value} onChange={handleChange}
          className="w-full p-2 border rounded text-sm">
          <option value="">Select {field.label}</option>
          {options.map(opt => (
            <option key={opt._id} value={opt._id}>
              {field.display ? field.display(opt) : opt._id}
            </option>
          ))}
        </select>
      );
    }

    if (field.type === 'select-enum') {
      return (
        <select value={value} onChange={handleChange}
          className="w-full p-2 border rounded text-sm">
          <option value="">Select {field.label}</option>
          {field.options.map(opt => (
            <option key={opt} value={opt}>{opt}</option>
          ))}
        </select>
      );
    }

    if (field.type === 'textarea') {
      return <textarea value={value} onChange={handleChange}
        className="w-full p-2 border rounded text-sm" rows="3" />;
    }

    return (
      <input type={field.type} value={value} readOnly={isReadonly}
        onChange={handleChange}
        className={`w-full p-2 border rounded text-sm ${isReadonly ? 'bg-gray-100' : ''}`} />
    );
  };

  // ========== SEARCH & PAGINATION ==========
  const filtered = useMemo(() => {
    if (!searchTerm) return manifests;
    const s = searchTerm.toLowerCase();
    return manifests.filter(m => {
      const route = routes.find(r => r._id === (m.routeid?._id || m.routeid));
      const routeStr = route ? `${route.originid?.areaname || ''} → ${route.destinationid?.locationname || ''}`.toLowerCase() : '';
      const coCourier = transports.find(t => t._id === m.cocourier)?.ownername || '';
      return (m.manifestno || '').toLowerCase().includes(s) ||
        routeStr.includes(s) ||
        coCourier.toLowerCase().includes(s);
    });
  }, [manifests, searchTerm, routes, transports]);

  const paginated = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  // ========== TOTALS ==========
  const selectedTotalBoxes = selectedBookings.reduce((sum, b) => sum + (b.noofbox || 0), 0);
  const selectedTotalWeight = selectedBookings.reduce((sum, b) => sum + (b.actweight || 0), 0).toFixed(1);

  // ========== RENDER ==========
  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <h2 className="text-2xl font-bold mb-6 text-gray-800">Outgoing Manifest Master</h2>

        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* Toolbar - Matches Pincode Style */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
            <div className="relative w-full md:w-80">
              <FaSearch className="absolute left-3 top-3 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search manifest, route, co-courier..."
                value={searchTerm}
                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:ring-2 focus:ring-blue-500 outline-none transition"
              />
            </div>

            <button
              onClick={() => { fetchBooked(); updatePreview(); setShowForm(true); }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium shadow-sm"
            >
              Add New Manifest
            </button>
          </div>

          {/* Table Section - Blue Header Design like Pincode Master */}
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="w-full table-auto border-collapse">
              <thead className="bg-blue-600 text-white">
                <tr>
                  <th className="p-3 text-left text-sm font-semibold uppercase tracking-wider">S.No</th>
                  <th className="p-3 text-left text-sm font-semibold uppercase tracking-wider">Manifest No</th>
                  <th className="p-3 text-left text-sm font-semibold uppercase tracking-wider">Date</th>
                  <th className="p-3 text-left text-sm font-semibold uppercase tracking-wider">Route Info</th>
                  <th className="p-3 text-left text-sm font-semibold uppercase tracking-wider">Transport / Mode</th>
                  <th className="p-3 text-left text-sm font-semibold uppercase tracking-wider">Status</th>
                  <th className="p-3 text-center text-sm font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-500 font-medium">Loading manifests...</td></tr>
                ) : paginated.length === 0 ? (
                  <tr><td colSpan={7} className="text-center py-12 text-gray-400 font-medium">No manifests found</td></tr>
                ) : (
                  paginated.map((m, i) => {
                    const route = routes.find(r => r._id === (m.routeid?._id || m.routeid));
                    const origin = route?.originid?.areaname || m.routeid?.originid?.areaname || 'N/A';
                    const destination = route?.destinationid?.areaname || m.routeid?.destinationid?.areaname || 'N/A';

                    const originAreaId = route?.originid?._id || route?.originid || m.routeid?.originid;
                    const destAreaId = route?.destinationid?._id || route?.destinationid || m.routeid?.destinationid;
                    const originAreaObj = areas.find(a => a._id === originAreaId);
                    const destAreaObj = areas.find(a => a._id === destAreaId);

                    const originPincode = originAreaObj?.pincode?.pincode || route?.originid?.pincode?.pincode || m.routeid?.originid?.pincode?.pincode || 'N/A';
                    const destPincode = destAreaObj?.pincode?.pincode || route?.destinationid?.pincode?.pincode || m.routeid?.destinationid?.pincode?.pincode || 'N/A';
                    const isOda = destAreaObj?.pincode?.isoda === 'ODA' || route?.destinationid?.pincode?.isoda === 'ODA' || m.routeid?.destinationid?.pincode?.isoda === 'ODA';
                    
                    const modeMap = { '1': 'Air', '2': 'Surface', '3': 'Train' };
                    const modeText = modeMap[m.modeid] || m.modeid || 'SURFACE';

                    // S.No Logic: Fixed search indexing consistency
                    const sNo = searchTerm
                      ? manifests.findIndex(orig => orig._id === m._id) + 1
                      : (currentPage - 1) * itemsPerPage + i + 1;

                    return (
                      <tr key={m._id} className="hover:bg-gray-50 transition-colors">
                        <td className="p-3 text-gray-600 font-medium">{sNo}</td>
                        <td className="p-3">
                          <span className="font-bold text-blue-700">{m.manifestno || 'N/A'}</span>
                        </td>
                        <td className="p-3 text-gray-700">{formatDate(m.ogdate)}</td>
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="font-semibold text-gray-800 uppercase">
                              {origin} → {destination}
                            </span>
                            {(originPincode !== 'N/A' || destPincode !== 'N/A') && (
                              <span className="text-xs text-gray-500 font-medium">
                                {originPincode} to {destPincode}
                                {isOda && <span className="text-red-500 ml-1">(ODA)</span>}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col">
                            <span className="text-sm font-medium">{m.vehiclenumber || 'N/A'}</span>
                            <span className="text-[10px] bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded self-start font-bold mt-1">
                              {modeText.toUpperCase()}
                            </span>
                          </div>
                        </td>
                        <td className="p-3">
                          <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${m.status?.includes('Delivered') ? 'bg-green-100 text-green-700' :
                              m.status?.includes('Intransit') ? 'bg-amber-100 text-amber-700' :
                                'bg-gray-100 text-gray-600'
                            }`}>
                            {m.status?.toUpperCase() || 'PENDING'}
                          </span>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-center gap-3">
                            <button onClick={() => { setSelectedManifest(m); setShowViewModal(true); }}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition" title="View">
                              <FaEye size={16} />
                            </button>
                            <button onClick={() => handleEdit(m)}
                              className="p-1.5 text-amber-600 hover:bg-amber-50 rounded transition" title="Edit">
                              <FaEdit size={16} />
                            </button>
                            <button onClick={() => handlePrint(m)}
                              className="p-1.5 text-green-600 hover:bg-green-50 rounded transition" title="Print PDF">
                              <FaPrint size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls - Pincode Style */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-6">
              <span className="text-sm text-gray-600 font-medium">
                Showing <span className="text-blue-600">{Math.min(filtered.length, (currentPage - 1) * itemsPerPage + 1)}</span> to <span className="text-blue-600">{Math.min(filtered.length, currentPage * itemsPerPage)}</span> of {filtered.length} entries
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ========== FORM MODAL ========== */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto p-6 shadow-xl">
              <h2 className="text-xl font-bold mb-6">
                {formData._id ? 'Edit Outgoing Manifest' : 'Create Outgoing Manifest'}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                {SCHEMA_FIELDS.map(field => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium mb-1">
                      {field.label} {['routeid', 'ogdate', 'ogtime'].includes(field.key) && '*'}
                    </label>
                    {renderField(field)}
                    {field.key === 'manifestno' && !formData._id && (
                      <p className="text-xs text-green-600 mt-1 font-medium">Next: {previewManifestNo}</p>
                    )}
                  </div>
                ))}
              </div>

              {/* Bookings Table */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-2">Select Bookings</h3>
                <input
                  type="text"
                  placeholder="Search by Docket Number"
                  onChange={e => fetchBooked(e.target.value)}
                  className="w-full p-2 border rounded mb-3 text-sm"
                />
                <div className="border rounded max-h-80 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100 sticky top-0">
                      <tr>
                        <th className="p-2 text-center"><input type="checkbox" onChange={e => setSelectedBookings(e.target.checked ? bookings : [])} /></th>
                        <th className="p-2">Docket No</th>
                        <th className="p-2">Origin</th>
                        <th className="p-2">Dest City</th>
                        <th className="p-2">Consignee</th>
                        <th className="p-2">Consignor</th>
                        <th className="p-2">Mode</th>
                        <th className="p-2">Pin</th>
                        <th className="p-2">Box</th>
                        <th className="p-2">Wt</th>
                        <th className="p-2">E-Way</th>
                        <th className="p-2">Remarks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map(b => (
                        <tr key={b._id} className={`border-b ${selectedBookings.some(sb => sb._id === b._id) ? 'bg-blue-50' : ''}`}>
                          <td className="p-2 text-center">
                            <input
                              type="checkbox"
                              checked={selectedBookings.some(sb => sb._id === b._id)}
                              onChange={() => {
                                setSelectedBookings(prev =>
                                  prev.some(sb => sb._id === b._id)
                                    ? prev.filter(sb => sb._id !== b._id)
                                    : [...prev, b]
                                );
                              }}
                            />
                          </td>
                          <td className="p-2 font-medium">{b.docketNumber}</td>
                          <td className="p-2">{b.pickrequestid?.location || b.fromLocation?.split(' (')[0] || 'N/A'}</td>
                          <td className="p-2">{b.topincode?.locationname || b.toLocation?.split(' (')[0] || 'N/A'}</td>
                          <td className="p-2">
                            <div className="flex items-center gap-1">
                              <input
                                type="text"
                                value={editedConsignors[b._id] || ''}
                                onChange={e => setEditedConsignors(prev => ({ ...prev, [b._id]: e.target.value }))}
                                className="w-full p-1 border rounded text-xs"
                                placeholder="Consignee"
                              />
                              <label className="cursor-pointer text-blue-600 hover:text-blue-800" title="Upload Image to Extract Text">
                                <FaUpload size={14} />
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  onChange={(e) => handleFileUpload(e, b._id)}
                                />
                              </label>
                            </div>
                          </td>
                          <td className="p-2">{b.pickrequestid?.clientid?.companyName || b.pickrequestid?.clientid?.name || b.consignorid?.companyName || b.consignorid?.name || b.consignor || 'N/A'}</td>
                          <td className="p-2">{b.mode}</td>
                          <td className="p-2">{b.topincode?.pincode}</td>
                          <td className="p-2 text-center">{b.noofbox}</td>
                          <td className="p-2">{b.actweight?.toFixed(1)}</td>
                          <td className="p-2">{b.ewaybill}</td>
                          <td className="p-2">
                            <input
                              type="text"
                              value={editedBookingRemarks[b._id] || ''}
                              onChange={e => setEditedBookingRemarks(prev => ({ ...prev, [b._id]: e.target.value }))}
                              className="w-full p-1 border rounded text-xs"
                              placeholder="Remarks"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="p-3 bg-gray-50 text-xs border-t">
                    <strong>Selected:</strong> {selectedBookings.length} |
                    <strong> Boxes:</strong> {selectedTotalBoxes} |
                    <strong> Weight:</strong> {selectedTotalWeight} kg
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3">
                <button onClick={handleCloseForm} className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm">
                  Cancel
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={isLoading || selectedBookings.length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 text-sm"
                >
                  {isLoading ? 'Saving...' : formData._id ? 'Update Manifest' : 'Create Manifest'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* View Modal */}
        {showViewModal && selectedManifest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
              <h2 className="text-xl font-bold mb-4">Manifest: {selectedManifest.manifestno}</h2>
              <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                {SCHEMA_FIELDS.map(f => {
                  let val = selectedManifest[f.key];
                  if (f.key === 'routeid') {
                    const r = routes.find(rt => rt._id === (val?._id || val)) || (typeof val === 'object' ? val : null);
                    const orig = r?.originid?.areaname || 'N/A';
                    const dest = r?.destinationid?.areaname || r?.destinationid?.locationname || 'N/A';
                    val = `${orig} → ${dest}`;
                  } else if (f.key === 'desthubid') {
                    const valId = val?._id || val;
                    const b = branches.find(br => br._id === valId);
                    val = b?.branchname || val?.branchname || val?.branchcode || 'N/A';
                  } else if (f.key === 'cocourier') {
                    const valId = val?._id || val;
                    const t = transports.find(tr => tr._id === valId);
                    val = t?.ownername || val?.ownername || (typeof val === 'string' ? val : 'N/A');
                  } else if (f.key === 'transportid') {
                    const valId = val?._id || val;
                    const t = transports.find(tr => tr._id === valId);
                    val = t?.vehicleno || val?.vehicleno || (typeof val === 'string' ? val : 'N/A');
                  }
                  return <p key={f.key}><strong>{f.label}:</strong> {val || 'N/A'}</p>;
                })}
              </div>
              <button onClick={() => setShowViewModal(false)} className="mt-4 px-4 py-2 bg-gray-500 text-white rounded text-sm">
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OutgoingManifest;